import { Box } from "@mui/material";
import { logger } from "@platform/browser-logger";
import { Alert, Button, Card, Typography } from "@platform/design-system";
import { useState } from "react";

import { TIER, TIER_IN_TEXT } from "../tier";

import { startLocalDevSession } from "./localDevSession";
import { signIn } from "./signIn";
import { loginWithAuth0 } from "./sso";

import type { ReactNode } from "react";

export const SIGN_IN_PAGE_TEST_ID = "golden-sign-in";

const startSignIn = async (returnTo: string): Promise<void> => {
  if (signIn.kind === "auth0") {
    await loginWithAuth0(signIn.tenant, returnTo);
    return;
  }
  // DEV first, so a production build drops the local session entirely.
  if (import.meta.env.DEV) startLocalDevSession();
};

/** Asks a signed-out visitor to sign in with their Centro account. */
export const SignInPage = (): ReactNode => {
  const [signingIn, setSigningIn] = useState(false);
  const [failed, setFailed] = useState(false);

  const onSignIn = async (): Promise<void> => {
    setSigningIn(true);
    setFailed(false);
    const returnTo = `${window.location.pathname}${window.location.search}`;
    try {
      await startSignIn(returnTo);
    } catch (error: unknown) {
      logger.error(
        "Starting sign-in failed",
        error instanceof Error ? error : new Error(String(error)),
      );
      setFailed(true);
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <Box
      data-testid={SIGN_IN_PAGE_TEST_ID}
      sx={{ display: "flex", justifyContent: "center", mt: 12, px: 2 }}
    >
      <Card
        title={`RUMI ${TIER} Set`}
        padding="spacious"
        sx={{ maxWidth: 480 }}
      >
        <Typography variant="body-medium" sx={{ mb: 3 }}>
          Sign in with your Centro account to review {TIER_IN_TEXT} RFPs and run
          RUMI against them.
        </Typography>
        {failed && (
          <Alert
            data-testid={`${SIGN_IN_PAGE_TEST_ID}-error`}
            severity="error"
            sx={{ mb: 3 }}
          >
            Sign-in could not start. Try again.
          </Alert>
        )}
        <Button
          data-testid={`${SIGN_IN_PAGE_TEST_ID}-button`}
          loading={signingIn}
          onClick={() => void onSignIn()}
        >
          Sign in
        </Button>
      </Card>
    </Box>
  );
};
